import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";

const DB_NAME = "grpc_clima_horario";
const DB_VERSION = 3;
const STORE_HORARIO = "horario";

/** Registro persistido: la última respuesta completa del rpc para una finca
 *  (se reemplaza entera en cada sync, no hay delta por item). */
type GrpcFincaClimaRecord<T> = {
  /** Id de la finca (`keyPath` del store). */
  fincaId: string;
  /** Fecha en que el worker volverá a sincronizar (`yyyy-MM-dd`). */
  proximaSincronizacion: string;
  /** Registros horarios de la finca, ordenados por fecha ascendente. */
  items: T[];
};

/** IndexedDB dedicada para el clima horario que trae `ClimaService` (gRPC-Web,
 *  ver CLAUDE.md del Server, sección migración a gRPC-Web): un solo registro
 *  por finca, sin índices ni items individuales con `id` (no hay delta). */
class GrpcFincaClimaHorarioDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_HORARIO, clave: "fincaId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** Registro cacheado de la finca, o `null` si nunca se ha sincronizado. */
  async get<T>(fincaId: string): Promise<GrpcFincaClimaRecord<T> | null> {
    const db = await this.open();
    const store = db.transaction(STORE_HORARIO, "readonly").objectStore(STORE_HORARIO);
    const record = await reqToPromise<GrpcFincaClimaRecord<T> | undefined>(
      store.get(fincaId),
    );
    return record ?? null;
  }

  /** Reemplaza el registro de la finca con la respuesta fresca del rpc. */
  async put<T>(
    fincaId: string,
    proximaSincronizacion: string,
    items: readonly T[],
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_HORARIO, "readwrite");
    tx.objectStore(STORE_HORARIO).put({
      fincaId,
      proximaSincronizacion,
      items: [...items],
    } satisfies GrpcFincaClimaRecord<T>);
    await txDone(tx);
  }

  /** Borra el registro cacheado de la finca: fuerza una sincronización en la
   *  próxima llamada. */
  async delete(fincaId: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_HORARIO, "readwrite");
    tx.objectStore(STORE_HORARIO).delete(fincaId);
    await txDone(tx);
  }
}

/** Singleton del store de clima horario por gRPC-Web. */
export const grpcFincaClimaHorarioDatabase = new GrpcFincaClimaHorarioDatabase();
